/**
 * FeatureAvailabilityContext — "does this surface exist in this facility
 * yet?", as distinct from "may this person use it" (that stays the RBAC
 * matrix mirror in `lib/route-permissions.ts`).
 *
 * Why this exists (#551 review):
 *
 * Frontends auto-deploy on merge via Vercel git-integration; the server
 * does NOT (Railway needs a manual deploy). So a merged admin page is live
 * for every existing tenant within seconds, whether or not the ops work it
 * depends on has happened. The vaccination inbox depends on a mailbox
 * Johnny has to provision (MX + Resend inbound route + two server env
 * vars). Its ingest half already failed closed on that config — its UI
 * half had no gate at all, so merging would have put a "Vaccination inbox"
 * item in HTHD's Operations menu, landing Kylie on a permanently-empty
 * queue whose copy told her to route vets to an address that does not
 * exist. That is the page's own P5 ("never lie about data") broken by its
 * own empty state.
 *
 * The gate is SERVER-DERIVED, never a second constant: `GET
 * /api/admin/vax-intake/config` answers from the same
 * `resolveVaxInboundConfig` predicate the webhook fails closed on, so the
 * surface and the ingest are dark or live together.
 *
 * Fail-closed everywhere: the initial value, an unauthenticated mount, a
 * failed probe and an in-flight probe all read as "not available". A
 * surface renders only after it has proven it exists.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ClientConfig } from '@ferro/config';
import { fetchVaxIntakeConfig, probeOwnerSettingsAvailable, readSession } from '../lib/api.js';
// Sawyer 16 staff-messaging (E4)
import { probeStaffMessagesAvailable } from '../lib/staff-messages-api.js';
import { seedUnread } from '../components/team-messages/useStaffMessagesUnread.js';
import { effectiveRolesOf } from '../lib/rbac-matrix.js';
import {
  gatedFeaturesVisibleTo,
  type FeatureAvailability,
} from '../lib/route-permissions.js';

type FeatureAvailabilityValue = {
  /** Which provisioning-gated surfaces exist here. Missing ⇒ not available. */
  features: FeatureAvailability;
  /**
   * The facility's inbound vaccination address, once provisioned. The
   * empty state names it instead of saying "the facility's vaccination
   * address" — a receptionist can only act on an address she can read.
   */
  vaxInboundAddress: string | null;
  /** True until the first probe settles. Consumers render nothing gated yet. */
  loading: boolean;
};

const EMPTY: FeatureAvailabilityValue = {
  features: {},
  vaxInboundAddress: null,
  loading: false,
};

/**
 * Default = nothing available. Standalone mounts (unit harnesses that
 * render a page without AppShell) therefore see every gated surface as
 * absent, which is the safe direction.
 */
const FeatureAvailabilityContext = createContext<FeatureAvailabilityValue>(EMPTY);

export function FeatureAvailabilityProvider({
  config,
  children,
}: {
  config: ClientConfig;
  children: ReactNode;
}): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [value, setValue] = useState<FeatureAvailabilityValue>({
    ...EMPTY,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const { token, staff } = readSession();
    // Probe only what could change what this staffer sees — nothing for no
    // session, and per feature only when their roles reach a surface gated
    // on it (a platform_admin renders PLATFORM_NAV, which has none). These
    // are the shell's only mount-time fetches; none fires to compute an
    // answer nobody will read.
    const wanted = gatedFeaturesVisibleTo(effectiveRolesOf(staff));
    if (!token || wanted.length === 0) {
      setValue({ ...EMPTY, loading: false });
      return;
    }
    // Neither probe rejects nor triggers the 401 auto-logout — see their
    // doc comments. They run in parallel; the shell waits for both.
    void Promise.all([
      wanted.includes('vaxInbox') ? fetchVaxIntakeConfig(apiUrl, token) : Promise.resolve(null),
      wanted.includes('ownerSettings')
        ? probeOwnerSettingsAvailable(apiUrl, token)
        : Promise.resolve(null),
      // Sawyer 16 staff-messaging (E4) — GET /staff-messages/unread: its 200 is "the server has
      // Team messages" AND the badge's first value (one request, not two).
      wanted.includes('staffMessages')
        ? probeStaffMessagesAvailable(apiUrl, token)
        : Promise.resolve(null),
    ]).then(([cfg, ownerSettings, staffMessages]) => {
      if (cancelled) return;
      if (staffMessages?.counts) seedUnread(staffMessages.counts);
      setValue({
        features: {
          ...(cfg ? { vaxInbox: cfg.enabled } : {}),
          ...(ownerSettings !== null ? { ownerSettings } : {}),
          ...(staffMessages !== null ? { staffMessages: staffMessages.available } : {}),
        },
        // Ted 10-09 — only an address the server says EMAIL INTAKE is on
        // for. The inbox page prints this; an address for a channel that is
        // off would bounce the vet's email (P5).
        vaxInboundAddress: cfg?.channels.email ? cfg.inboundAddress : null,
        loading: false,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const memo = useMemo(() => value, [value]);
  return (
    <FeatureAvailabilityContext.Provider value={memo}>
      {children}
    </FeatureAvailabilityContext.Provider>
  );
}

export function useFeatureAvailability(): FeatureAvailabilityValue {
  return useContext(FeatureAvailabilityContext);
}
